// Quake 3 MD3 model parser (`IDP3` version 15) + `.skin` remapping.
//
// MD3 is Quake's static/segment model format: a set of surfaces (one shader
// each) with per-frame vertex positions stored as 16-bit values scaled by
// 1/64. We only need positions + texcoords for unlit rendering (models are lit
// by the lightgrid ambient/diffuse at the entity origin, not a lightmap).
//
// Layouts (all little-endian):
//   header   108 B: ident, version, name[64], flags, numFrames, numTags,
//                    numSurfaces, numSkins, ofsFrames, ofsTags, ofsSurfaces, ofsEnd
//   frame     56 B: mins[3], maxs[3], origin[3], radius, name[16]
//   tag      112 B: name[64], origin[3], axis[9]
//   surface  108 B: ident, name[64], flags, numFrames, numShaders, numVerts,
//                    numTriangles, ofsTriangles, ofsShaders, ofsSt,
//                    ofsXyzNormals, ofsEnd
//   shader    68 B: name[64], shaderIndex
//   xyznormal  8 B: xyz[3] i16, normal i16   (per vertex, per frame)
//
// Tags are stored as `numTags * numFrames` entries indexed
// `tagIndex * numFrames + frame`.

const MD3_IDENT = 0x33504449; // "IDP3"
const MD3_VERSION = 15;
const MD3_XYZ_SCALE = 1.0 / 64.0;

const FRAME_SIZE = 56;
const TAG_SIZE = 112;
const SHADER_SIZE = 68;

export interface Md3Frame {
  name: string;
  mins: [number, number, number];
  maxs: [number, number, number];
  origin: [number, number, number];
}

export interface Md3Tag {
  name: string;
  origin: [number, number, number];
  /** 3×3 rotation matrix, column-major (axis[0..2] = forward/right/up). */
  axis: number[];
}

export interface Md3Surface {
  name: string;
  shaderNames: string[];
  numVerts: number;
  numFrames: number;
  /** Triangle indices into the surface's vertices. */
  indices: Uint32Array;
  /** Per-vertex texture coords (`numVerts * 2`). */
  st: Float32Array;
  /**
   * All frames' positions concatenated (`numFrames * numVerts * 3` floats).
   * Frame `f`, vertex `v` at `(f*numVerts + v)*3`.
   */
  positions: Float32Array;
}

export interface Md3Model {
  name: string;
  numFrames: number;
  frames: Md3Frame[];
  /** `tags[tagIndex][frame]`. */
  tags: Md3Tag[][];
  surfaces: Md3Surface[];
}

function cstr(bytes: Uint8Array, off: number, len: number): string {
  let end = off;
  const stop = off + len;
  while (end < stop && bytes[end] !== 0) end++;
  return new TextDecoder("latin1").decode(bytes.subarray(off, end));
}

export function parseMd3(buffer: ArrayBuffer | Uint8Array): Md3Model {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (dv.getInt32(0, true) !== MD3_IDENT) throw new Error("not an MD3 (bad ident)");
  const version = dv.getInt32(4, true);
  if (version !== MD3_VERSION) throw new Error(`unsupported MD3 version ${version}`);

  const name = cstr(bytes, 8, 64);
  const numFrames = dv.getInt32(76, true);
  const numTags = dv.getInt32(80, true);
  const numSurfaces = dv.getInt32(84, true);
  const ofsFrames = dv.getInt32(92, true);
  const ofsTags = dv.getInt32(96, true);
  const ofsSurfaces = dv.getInt32(100, true);

  // Frames.
  const frames: Md3Frame[] = [];
  for (let f = 0; f < numFrames; f++) {
    const o = ofsFrames + f * FRAME_SIZE;
    frames.push({
      mins: [dv.getFloat32(o, true), dv.getFloat32(o + 4, true), dv.getFloat32(o + 8, true)],
      maxs: [dv.getFloat32(o + 12, true), dv.getFloat32(o + 16, true), dv.getFloat32(o + 20, true)],
      origin: [dv.getFloat32(o + 24, true), dv.getFloat32(o + 28, true), dv.getFloat32(o + 32, true)],
      name: cstr(bytes, o + 40, 16),
    });
  }

  // Tags (tagIndex * numFrames + frame).
  const tags: Md3Tag[][] = [];
  for (let t = 0; t < numTags; t++) {
    const perTag: Md3Tag[] = [];
    for (let f = 0; f < numFrames; f++) {
      const o = ofsTags + (t * numFrames + f) * TAG_SIZE;
      const axis: number[] = [];
      for (let k = 0; k < 9; k++) axis.push(dv.getFloat32(o + 76 + k * 4, true));
      perTag.push({
        name: cstr(bytes, o, 64),
        origin: [dv.getFloat32(o + 64, true), dv.getFloat32(o + 68, true), dv.getFloat32(o + 72, true)],
        axis,
      });
    }
    tags.push(perTag);
  }

  // Surfaces.
  const surfaces: Md3Surface[] = [];
  let surfOff = ofsSurfaces;
  for (let s = 0; s < numSurfaces; s++) {
    if (dv.getInt32(surfOff, true) !== MD3_IDENT) throw new Error("bad MD3 surface ident");
    // Surface sub-offsets (triangles/shaders/st/xyz/end) are relative to the
    // surface start, not the file.
    const base = surfOff;
    const sName = cstr(bytes, base + 4, 64);
    const sFrames = dv.getInt32(base + 72, true);
    const numShaders = dv.getInt32(base + 76, true);
    const numVerts = dv.getInt32(base + 80, true);
    const numTriangles = dv.getInt32(base + 84, true);
    const ofsTriangles = base + dv.getInt32(base + 88, true);
    const ofsShaders = base + dv.getInt32(base + 92, true);
    const ofsSt = base + dv.getInt32(base + 96, true);
    const ofsXyzNormals = base + dv.getInt32(base + 100, true);
    const ofsEnd = dv.getInt32(base + 104, true);
    surfOff = base + ofsEnd; // surfaces are back-to-back

    const shaderNames: string[] = [];
    for (let i = 0; i < numShaders; i++) {
      shaderNames.push(cstr(bytes, ofsShaders + i * SHADER_SIZE, 64));
    }

    const indices = new Uint32Array(numTriangles * 3);
    for (let i = 0; i < numTriangles * 3; i++) {
      indices[i] = dv.getUint32(ofsTriangles + i * 4, true);
    }

    const st = new Float32Array(numVerts * 2);
    for (let i = 0; i < numVerts; i++) {
      st[i * 2] = dv.getFloat32(ofsSt + i * 8, true);
      st[i * 2 + 1] = dv.getFloat32(ofsSt + i * 8 + 4, true);
    }

    const positions = new Float32Array(sFrames * numVerts * 3);
    for (let f = 0; f < sFrames; f++) {
      for (let v = 0; v < numVerts; v++) {
        const o = ofsXyzNormals + (f * numVerts + v) * 8;
        const d = ((f * numVerts) + v) * 3;
        positions[d] = dv.getInt16(o, true) * MD3_XYZ_SCALE;
        positions[d + 1] = dv.getInt16(o + 2, true) * MD3_XYZ_SCALE;
        positions[d + 2] = dv.getInt16(o + 4, true) * MD3_XYZ_SCALE;
      }
    }

    surfaces.push({ name: sName, shaderNames, numVerts, numFrames: sFrames, indices, st, positions });
  }

  return { name, numFrames, frames, tags, surfaces };
}

/**
 * Parse a Quake `.skin` file into a surface-name → shader-name map. Lines are
 * `surface,shader`; `//` comments and blank lines are ignored. `tag_*` entries
 * (tag remaps) are skipped — tags aren't skinned yet.
 */
export function parseSkin(text: string): Map<string, string> {
  const map = new Map<string, string>();
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.replace(/\/\/.*$/, "").trim();
    if (!line) continue;
    const comma = line.indexOf(",");
    if (comma < 0) continue;
    const surface = line.slice(0, comma).trim().toLowerCase();
    const shader = line.slice(comma + 1).trim();
    if (!surface || !shader || surface.startsWith("tag_")) continue;
    map.set(surface, shader);
  }
  return map;
}
